import React, { useCallback, useState } from 'react';
import {
  Flex,
  FlexProps,
  Icon,
  IconButton,
  InputProps,
  useMultiStyleConfig,
} from '@chakra-ui/react';

import { ReactComponent as SearchIcon } from 'resources/images/search.svg';
import { ReactComponent as CloseIcon } from 'resources/images/close.svg';

import debounce from 'lodash/debounce';
import { SEARCH_DEBOUNCE_MS } from 'app/components/Song/SearchBar/constants';
import { InputField } from 'app/components/Core';

export interface SearchBarProps {
  className?: string;
  variant?: 'animated' | 'simple';
  containerProps?: FlexProps;
  textFieldProps?: Partial<InputProps & { 'data-testid': string }>;
  placeholder?: string;
  onSearch: (param: string) => void;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  className,
  variant,
  containerProps,
  textFieldProps,
  placeholder = 'Search',
  onSearch,
}) => {
  const styles = useMultiStyleConfig('SearchBar', { variant });

  const [inputValue, setInputValue] = useState('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce(onSearch, SEARCH_DEBOUNCE_MS, { trailing: true }),
    []
  );

  const clearInput = () => {
    setInputValue('');
    debouncedSearch('');
  };

  const onInputChange = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    const trimmedValue = value.trim();
    setInputValue(value);
    debouncedSearch(trimmedValue);
  };

  return (
    <Flex className={className} sx={styles.container} {...containerProps}>
      <InputField
        placeholder={placeholder}
        value={inputValue}
        sx={styles.input}
        leftElement={
          <Icon __css={styles.searchIcon}>
            <SearchIcon />
          </Icon>
        }
        rightElement={
          inputValue.length ? (
            <IconButton
              variant="unstyled"
              icon={<CloseIcon />}
              aria-label="Clear icon"
              data-testid="clear-input"
              sx={styles.closeIcon}
              onClick={clearInput}
            />
          ) : undefined
        }
        onChange={onInputChange}
        {...textFieldProps}
      />
    </Flex>
  );
};
